import { useEffect } from 'react';

import { objectKeysToCamelCase } from '@mint-lib/api';
import { useTranslation } from '@mint-lib/i18n';

import { KeysToSnakeCase } from '@myn/shared/src/utils';

import type { AsyncUploadMessage, CanonicalAPIError } from '../api/types.js';
import { getUploadFileErrorMessage } from '../constants/errorsMapping.js';
import {
  abandonUploadOperation,
  failUploadOperation,
  succeedUploadOperation,
} from '../operations/uploadOperation.js';
import {
  QueryKeys,
  useDataFilePreview,
  useTypedQueryClient,
  useUploadDeferredPromise,
} from '../queries/index.js';
import { useDataFileSocket } from './useDataFileSocket.js';

export const useAsyncUpload = ({ dataFileId }: { dataFileId: string }) => {
  const { t } = useTranslation('@myn/import-hub');
  const queryClient = useTypedQueryClient();

  const dataFileSocket = useDataFileSocket(dataFileId);

  const { getDeferred } = useUploadDeferredPromise(dataFileId);
  const { refetch: refetchDataFilePreview } = useDataFilePreview(dataFileId);
  const uploadStateQueryKey = QueryKeys.dataFile.steps.upload.state(dataFileId);

  const onUploadProgress = (response: KeysToSnakeCase<AsyncUploadMessage>) => {
    const data = objectKeysToCamelCase(response);
    queryClient.setQueryData(uploadStateQueryKey, {
      ...data,
      status: 'uploading',
    });
    getDeferred().resolve();
  };

  const onUploadSuccess = () => {
    succeedUploadOperation(dataFileId);
    queryClient.setQueryData(uploadStateQueryKey, { status: 'finished' });
    getDeferred().resolve();
    refetchDataFilePreview();
  };

  const onUploadFailed = (error: CanonicalAPIError) => {
    failUploadOperation(dataFileId, error.type);
    queryClient.setQueryData(uploadStateQueryKey, { status: 'failed' });
    getDeferred().reject(new Error(getUploadFileErrorMessage(t, error)));
    refetchDataFilePreview();
  };

  // Leaving the wizard releases the socket, so the parse result of an upload
  // still running (the dialog closes on the first progress event) can no
  // longer end its `import-hub.upload` operation.
  useEffect(() => () => abandonUploadOperation(dataFileId), [dataFileId]);

  useEffect(() => {
    if (!dataFileSocket) {
      return () => null;
    }

    dataFileSocket.on('upload_progress', onUploadProgress);
    dataFileSocket.on('upload_finished', onUploadSuccess);
    dataFileSocket.on('upload_failed', onUploadFailed);

    return () => {
      dataFileSocket.off('upload_progress');
      dataFileSocket.off('upload_finished');
      dataFileSocket.off('upload_failed');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFileSocket]);
};
